import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import type { Delta } from '../../utils/monthBuckets';

interface DashboardMetricCardProps {
  label: ReactNode;
  value: string;
  icon: LucideIcon;
  delta?: Delta | null;
  invertDelta?: boolean;
  progressPct?: number | null;
  accent?: 'blue' | 'green' | 'red' | 'yellow';
  onClick?: () => void;
  clickableHint?: string;
}

const ACCENT_RING: Record<NonNullable<DashboardMetricCardProps['accent']>, string> = {
  blue: 'from-blueprint-blue/8 to-transparent',
  green: 'from-galvanizing-green/10 to-transparent',
  red: 'from-hero-red/10 to-transparent',
  yellow: 'from-thin-yellow/20 to-transparent',
};

const ICON_BG: Record<NonNullable<DashboardMetricCardProps['accent']>, string> = {
  blue: 'bg-[rgba(0,39,63,0.06)] text-blueprint-blue',
  green: 'bg-[rgba(31,154,58,0.08)] text-galvanizing-green',
  red: 'bg-[rgba(196,0,31,0.06)] text-readable-red',
  yellow: 'bg-[rgba(255,202,87,0.15)] text-[#B38600]',
};

function deltaColour(direction: Delta['direction'], invert: boolean): string {
  if (direction === 'flat') return 'text-heritage-grey';
  const good = invert ? direction === 'down' : direction === 'up';
  return good ? 'text-galvanizing-green' : 'text-readable-red';
}

export const DashboardMetricCard = ({
  label,
  value,
  icon: Icon,
  delta,
  invertDelta = false,
  progressPct,
  accent = 'blue',
  onClick,
  clickableHint,
}: DashboardMetricCardProps) => {
  const Arrow =
    delta?.direction === 'up' ? ArrowUpRight : delta?.direction === 'down' ? ArrowDownRight : Minus;
  const deltaText =
    delta?.pct != null ? `${delta.pct > 0 ? '+' : ''}${delta.pct.toFixed(1)}%` : null;

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={clsx(
        'group relative overflow-hidden rounded-2xl border border-[#E2E5EA] bg-white p-4 shadow-sm',
        'transition duration-200 hover:-translate-y-0.5 hover:shadow-md',
        onClick && 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-hero-red-20',
      )}
    >
      <div
        className={clsx(
          'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-80',
          ACCENT_RING[accent],
        )}
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-heritage-grey">
            {label}
          </p>
          <p className="mt-1 truncate font-heading text-2xl font-bold text-blueprint-blue">{value}</p>
          {deltaText && (
            <div
              className={clsx(
                'mt-2 flex items-center gap-1 text-xs font-medium',
                deltaColour(delta!.direction, invertDelta),
              )}
            >
              <Arrow className="h-3.5 w-3.5" />
              <span>{deltaText}</span>
              <span className="text-heritage-grey">vs last month</span>
            </div>
          )}
        </div>
        <div
          className={clsx(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
            ICON_BG[accent],
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {progressPct != null && (
        <div className="relative mt-3 h-1.5 overflow-hidden rounded-full bg-[#EEF0F3]">
          <div
            className="h-full rounded-full bg-galvanizing-green transition-all duration-500"
            style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }}
          />
        </div>
      )}
      {onClick && clickableHint && (
        <p className="relative mt-2 text-[10px] font-medium uppercase tracking-wider text-heritage-grey">
          {clickableHint}
        </p>
      )}
    </div>
  );
};
